<template>
  <div class="py-8">
    <v-app-bar flat color="transparent">
      <app-bar-nav-icon />
      <v-toolbar-title class="breadcrumbs d-flex align-center">
        <v-icon class="text-light-blue">{{ icons.arrowLeft }}</v-icon>
        <router-link
          class="text-decoration--none"
          :to="{ name: 'business-admin' }"
          >Business Admin</router-link
        >
      </v-toolbar-title>
    </v-app-bar>

    <div class="ma-4" v-if="form">
      <div class="display-2 font-weight-bold my-6 text-capitalize">
        {{ displayName }}
      </div>

      <div class="d-flex my-6">
        <v-tabs hide-slider class="user-info-tabs">
          <v-tab class="pa-0" v-for="(item, key) in tabItems" :key="key">
            {{ item.tab }}
          </v-tab>

          <v-tab-item class="col-md-4 col-sm-12 col-xs-12 mt-6 pa-0">
            <v-alert
              dense
              outlined
              type="warning"
              color="orange darken-3"
              max-width="400px"
              v-if="!!isBusinessAdminBlocked"
            >
              This account is <strong>disabled</strong>.
            </v-alert>

            <avatar-form
              :user="businessAdmin"
              @change="avatarChange"
              @avatarRemove="businessAdminAvatarRemoved"
              :avatarLoading="avatarLoading"
            />

            <v-form ref="form" v-if="form" class="business-admin-form">
              <v-divider></v-divider>

              <h3 class="py-5 primary--text">Business Admin Information</h3>

              <label class="text-field-label">Full Name</label>
              <v-text-field
                flat
                solo
                required
                class="mt-2"
                v-model="form.full_name"
                :error-messages="form.$getError('full_name')"
                :loading="loading"
              ></v-text-field>

              <label class="text-field-label">Email</label>
              <v-text-field
                flat
                solo
                required
                class="mt-2"
                v-model="form.email"
                :error-messages="form.$getError('email')"
                :loading="loading"
              >
                <v-tooltip slot="append" top v-if="businessAdmin.email">
                  <template v-slot:activator="{ on }">
                    <v-icon
                      color="success"
                      v-on="on"
                      v-if="businessAdmin.email_verified"
                      >{{ icons.check }}</v-icon
                    >
                    <v-icon color="warning" v-on="on" v-else>{{
                      icons.alert
                    }}</v-icon>
                  </template>
                  <span v-if="businessAdmin.email_verified"
                    >Email Verified</span
                  >
                  <span v-else>Unverified email</span>
                </v-tooltip>
              </v-text-field>

              <label class="text-field-label">Phone Number</label>
              <v-text-field
                flat
                solo
                required
                class="mt-2"
                v-model="form.phone_number"
                :error-messages="form.$getError('phone_number')"
                :loading="loading"
              >
              </v-text-field>

              <label class="text-field-label">USI Number</label>
              <v-text-field
                flat
                solo
                required
                class="mt-2"
                v-model="form.usi"
                :error-messages="form.$getError('usi')"
                :loading="loading"
              >
              </v-text-field>

              <label class="text-field-label">Company</label>
              <v-autocomplete
                flat
                solo
                clearable
                item-value="id"
                item-text="name"
                placeholder="Search company..."
                v-model="form.company_id"
                :items="companies"
                :loading="companiesLoading"
                :disabled="companiesLoading"
                :error-messages="form.$getError('company_id')"
                :auto-select-first="false"
                :search-input.sync="companiesSearch"
              ></v-autocomplete>

              <v-checkbox
                :input-value="isBusinessAdminBlocked"
                label="Disable business admin account"
                color="orange darken-3"
                class="mt-0"
                @mousedown="showModal = true"
              ></v-checkbox>

              <div class="mt-4">
                <v-btn
                  color="primary"
                  class="mr-4 px-6"
                  height="40px"
                  width="100%"
                  @click="updateBusinessAdminDetails"
                  :loading="form.$busy"
                  >Update</v-btn
                >
              </div>
            </v-form>

            <v-dialog v-model="deleteDialog" max-width="290">
              <v-card>
                <v-card-title class="headline">Delete ?</v-card-title>

                <v-card-text
                  >Are you sure you want to delete this Business Admin
                  ?</v-card-text
                >

                <v-card-actions>
                  <v-spacer></v-spacer>

                  <v-btn
                    text
                    @click="deleteDialog = false"
                    :loading="form.$busy"
                    >Cancel</v-btn
                  >

                  <v-btn
                    color="red"
                    text
                    @click="deleteBusinessAdmin"
                    :loading="form.$busy"
                    >Yes, Delete please</v-btn
                  >
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-tab-item>

          <v-tab-item><FileCards :id="businessAdmin.id"/></v-tab-item>
        </v-tabs>
      </div>
    </div>

    <!-- <test-notifications v-if="businessAdmin" class="pa-4" :user="businessAdmin" /> -->

    <v-overlay absolute :value="loading" opacity="0.2">
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
      ></v-progress-circular>
    </v-overlay>
    <v-snackbar v-model="snackbar.show" right :color="snackbar.color">
      {{ snackbar.message }}
      <v-btn text @click="snackbar.show = false">OK</v-btn>
    </v-snackbar>

    <ConfirmModal
      v-model="showModal"
      title="Disable Account"
      :message="disableMessage"
      @cancel="showModal = false"
      @confirm="blockAccount"
    ></ConfirmModal>
  </div>
</template>

<script>
import { mdiChevronLeft, mdiCheck, mdiAlertCircleOutline } from '@mdi/js'
import { mapState, mapActions, mapMutations } from 'vuex'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import AvatarForm from './../components/AvatarForm'
// import TestNotifications from '@/components/TestNotifications'
import ConfirmModal from '@/components/modals/ConfirmModal'
import FileCards from './../components/FileCards'

import Form from '@/utils/form'

import { find } from 'lodash'

export default {
  name: 'BusinessAdminDetails',

  components: {
    AvatarForm,
    AppBarNavIcon,
    // TestNotifications,
    ConfirmModal,
    FileCards
  },

  data() {
    return {
      icons: {
        arrowLeft: mdiChevronLeft,
        check: mdiCheck,
        alert: mdiAlertCircleOutline
      },
      loading: false,
      avatarLoading: false,
      companiesLoading: false,
      companiesSearch: '',
      companies: [],
      deleteDialog: false,
      form: null,
      showCropperModal: false,
      avatarTimestamp: new Date().getTime(),
      snackbar: {
        show: false,
        message: null,
        color: ''
      },
      showModal: false,
      tabItems: [{ tab: 'Info' }, { tab: 'Files' }]
    }
  },

  computed: {
    ...mapState({
      businessAdmin: state => state.businessAdmin.businessAdminDetails,
      authUser: state => state.auth.user
    }),
    displayName() {
      return this.form.full_name || this.form.email
    },
    businessAdminBlockedAction() {
      return this.businessAdmin
        ? this.businessAdmin.blocked_at
          ? 'unblocked'
          : 'blocked'
        : 'unblocked'
    },
    isBusinessAdminBlocked() {
      return this.businessAdmin
        ? this.businessAdmin.blocked_at
          ? this.businessAdmin.blocked_at
          : null
        : null
    },
    disableMessage() {
      const action = this.businessAdminBlockedAction
      return `Are you sure you want ${action} this account?`
    }
  },

  methods: {
    ...mapActions({
      getBusinessAdminDetails: 'businessAdmin/getBusinessAdminDetails',
      changeAvatar: 'businessAdmin/changeAvatar',
      updateBusinessAdmin: 'businessAdmin/updateBusinessAdmin',
      blockBusinessAdmin: 'businessAdmin/blockBusinessAdmin',
      getCompanies: 'company/getCompanies'
    }),

    ...mapMutations({
      clearBusinessAdminDetails: 'businessAdmin/clearBusinessAdminDetails',
      setSelectedTradieAvatar: 'businessAdmin/setSelectedTradieAvatar',
      businessAdminAvatarRemoved: 'businessAdmin/businessAdminAvatarRemoved',
      clearFileList: 'file/clearFileList'
    }),

    async getBusinessAdmin() {
      this.loading = true
      await this.getBusinessAdminDetails(this.$route.params.id)
      this.form = new Form(this.businessAdmin)
      this.loading = false
    },

    async avatarChange(image) {
      this.avatarLoading = true
      let data = new FormData()
      data.append('user_id', this.businessAdmin.id)
      data.append('avatar', image)
      await this.changeAvatar(data)
      this.avatarLoading = false
      this.showSnackbar('Avatar successfully updated!', 'success')
    },

    async updateBusinessAdminDetails() {
      this.form.$busy = true
      this.form.$clearErrors()
      this.updateBusinessAdmin(this.form.$data())
        .then(() => {
          this.form.$busy = false
          this.showSnackbar(
            'Business admin details successfully updated!',
            'success'
          )
        })
        .catch(err => {
          this.form.$busy = false
          if (err.response.status === 422) {
            this.form.$setErrors(err.response.data.errors)
          }
        })
    },

    deleteBusinessAdmin() {
      this.form.$busy = true
      this.businessAdmin.delete().then(() => {
        this.form.$busy = false
        this.deleteDialog = false
        this.showSnackbar('Business admin deleted successfully!')
        this.$router.replace('/users/business-admin')
      })
    },

    showSnackbar(message, color) {
      this.snackbar.message = message
      this.snackbar.show = true
      this.snackbar.color = color
    },
    blockAccount(value) {
      this.form.$busy = true
      const action = this.businessAdminBlockedAction
      this.blockBusinessAdmin({
        id: this.businessAdmin.id,
        action
      })
        .then(() => {
          this.form.$busy = false
          this.showModal = false
          this.showSnackbar(`Business Admin successfully ${action}!`, 'success')
        })
        .catch(err => {
          this.form.$busy = false
          if (err.response.status === 422) {
            this.form.$setErrors(err.response.data.errors)
          }
        })
    },
    async fetchCompanies() {
      this.companiesLoading = true

      await this.getCompanies().then(res => {
        if (res) {
          this.companies = res
        }
      })

      this.companiesLoading = false
    },
    setCompany() {
      if (this.companies) {
        const companyExist = find(this.companies, company => {
          return company.id === this.form.company_id
        })

        if (companyExist) {
          this.search = companyExist.name
        }
      }
    },
    clearCompanies() {
      this.companies = []
    }
  },

  created() {
    this.getBusinessAdmin()
    this.fetchCompanies()
  },

  destroyed() {
    this.clearBusinessAdminDetails()
    this.clearCompanies()
    this.clearFileList()
  },

  watch: {
    $route() {
      this.getBusinessAdmin()
      this.fetchCompanies()
    }
  }
}
</script>

<style lang="scss" scoped>
.business-admin-form {
  width: 400px;

  @media (max-width: 420px) {
    width: 100%;
  }
}
</style>
